import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import ShohAI from '../components/ShohAI';
import WorkScheduleCalendar from '../components/WorkScheduleCalendar';
import ActiveShiftView from '../components/ActiveShiftView';
import axios from 'axios';
import { getFileUrl } from '../config/api';
import { toLocalDateString, parseLocalDate, getDayName, getTodayLocal } from '../utils/dateUtils';
import PendingReviews from '../components/PendingReviews';
import CountdownTimer from '../components/CountdownTimer';

interface Job {
  _id: string;
  title: string;
  description: string;
  serviceType: string;
  location: {
    address: string;
    city: string;
    coordinates?: [number, number]; // [longitude, latitude]
  };
  schedule: {
    startDate: string;
    endDate: string;
    preferredTime: string;
  };
  visitSchedule?: Array<{
    _id: string;
    date: string;
    isVisitDay: boolean;
    notes?: string;
    time?: string;
    status?: string;
    actualStartTime?: string;
    actualEndTime?: string;
  }>;
  contract: {
    type: string;
    rate: number;
  };
  customer: {
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
    profilePhoto?: string;
  };
  status: string;
}

interface NextWorkDay {
  date: Date;
  job: Job;
  customerCancelled: boolean;
  notes?: string;
  time?: string;
  visitId?: string;
  status?: string;
  visit?: any;
  canStart?: boolean;
}

const CaregiverDashboard: React.FC = () => {
  usePageTitle('Caregiver Dashboard');
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [myJobs, setMyJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [autoFilledPin, setAutoFilledPin] = useState<string>('');

  useEffect(() => {
    fetchMyJobs();
  }, []);

  const fetchMyJobs = async () => {
    try {
      if (!user?.id) return;
      const response = await axios.get(`/api/jobs/caregiver/${user.id}`);
      setMyJobs(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching my jobs:', error);
      setLoading(false);
    }
  };

  const getServiceBadgeColor = (type: string): string => {
    const colors: Record<string, string> = {
      'nurse-care': 'bg-red-100 text-red-800',
      'everyday-assistance': 'bg-primary-100 text-primary-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

    const getNextWorkDay = (): NextWorkDay | null => {
    const activeJobs = myJobs.filter(job => job.status === 'in-progress' || job.status === 'accepted');
    
    if (activeJobs.length === 0) {
      return null;
    }

    const today = getTodayLocal();

    let nextWorkDay: NextWorkDay | null = null;
    let earliestDate: Date | null = null;

    for (const job of activeJobs) {
      const startDate = parseLocalDate(job.schedule.startDate);
      const endDate = parseLocalDate(job.schedule.endDate);
      
      // Check each day from today to end date
      const checkStartDate = today > startDate ? today : startDate;
      for (let d = new Date(checkStartDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          
          // Get local date string for comparison
          const dateStr = toLocalDateString(d);
          
          // Check if customer has cancelled this specific day
          const visitScheduleEntry = job.visitSchedule?.find((vs: any) => {
            // Use local date string utility for comparison
            const vsDateStr = toLocalDateString(vs.date);
            return vsDateStr === dateStr;
          });
          
          // Check if this day is cancelled - if visitScheduleEntry exists and isVisitDay is false, skip it
          if (visitScheduleEntry && visitScheduleEntry.isVisitDay === false) {
            continue; // This day is cancelled, check next day
          }
          
          // If shift completed or ended, skip it
          if (visitScheduleEntry?.status === 'completed' || visitScheduleEntry?.actualEndTime) {
             continue;
          }

          if (!earliestDate || d < earliestDate) {
            // Logic to determine if "Start Shift" should be shown
            const isToday = dateStr === toLocalDateString(today);
            let isTimeReached = false;
            
            console.log('Checking canStart logic:', {
              dateStr,
              todayStr: toLocalDateString(today),
              isToday,
              visitTime: visitScheduleEntry?.time,
              preferredTime: job.schedule.preferredTime
            });
            
            if (isToday) {
               const timeString = visitScheduleEntry?.time || job.schedule.preferredTime;
               if (timeString) {
                  try {
                    const parts = timeString.split('-');
                    const startStr = parts[0].trim();
                    const endStr = parts[1] ? parts[1].trim() : null;

                    const parseTimeDate = (str: string) => {
                         let [time, period] = str.split(' ');
                         let h, m;
                         const tMap = time.split(':').map(Number);
                         h = tMap[0];
                         m = tMap[1] || 0;
                         
                         if (period === 'PM' && h !== 12) h += 12;
                         if (period === 'AM' && h === 12) h = 0;
                         
                         const d = new Date();
                         d.setHours(h, m, 0, 0);
                         return d;
                    };

                    const now = new Date();
                    const shiftStart = parseTimeDate(startStr);
                    let shiftEnd = endStr ? parseTimeDate(endStr) : null;
                    
                    // Handle overnight
                    if (shiftEnd && shiftEnd < shiftStart) {
                        shiftEnd.setDate(shiftEnd.getDate() + 1);
                    }
                    
                    console.log('Time check:', {
                      timeString,
                      shiftStart: shiftStart.toLocaleTimeString(),
                      shiftEnd: shiftEnd?.toLocaleTimeString(),
                      currentTime: now.toLocaleTimeString(),
                    });
                    
                    if (now >= shiftStart) {
                        isTimeReached = true;
                        if (shiftEnd && now > shiftEnd) {
                            isTimeReached = false;
                        }
                    }
                  } catch (e) {
                    console.error("Error parsing time string:", timeString, e);
                    // Fallback: if parsing fails, assume time reached to avoid blocking
                    isTimeReached = true;
                  }
               } else {
                 // No time specified, allow start anytime on the day
                 isTimeReached = true;
               }
            }

            const canStart = isToday && isTimeReached;
            
            console.log('Final canStart result:', canStart);

            earliestDate = new Date(d);

            nextWorkDay = {
              date: new Date(d),
              job: job,
              customerCancelled: false,
              notes: visitScheduleEntry?.notes,
              time: visitScheduleEntry?.time || job.schedule.preferredTime,
              visitId: visitScheduleEntry?._id,
              status: visitScheduleEntry?.status,
              visit: visitScheduleEntry,
              canStart: canStart
            };
            
            // Found the next visit for this job, so stop checking dates for this job
            // and move to next job to see if it has an earlier visit
            break; 
          }
      }
    }

    return nextWorkDay;
  };

  const handleStartShift = async (jobId: string, visitId: string) => {
    try {
      // DEVELOPMENT ONLY: Bypass location check
      const location = { lat: 23.8103, lng: 90.4125 }; // Dhaka dummy coords
      
      const token = localStorage.getItem('token');
      await axios.post(`/api/shifts/${jobId}/shift/start`, {
        visitId: visitId,
        location
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/jobs/${jobId}`);
    } catch (error) {
       console.error("Failed to start shift", error);
       alert('Failed to start shift. Please try again.');
    }
  };

  const nextWork = getNextWorkDay();

  // Helper to get Date object for start time
  const getStartTimeDate = (date: Date, timeString?: string) => {
      if (!timeString) return null;
      try {
          const startPart = timeString.split('-')[0].trim();
          const [time, period] = startPart.split(' ');
          let [hours, minutes] = time.split(':').map(Number);
          
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          
          const target = new Date(date);
          target.setHours(hours, minutes, 0, 0);
          return target;
      } catch (e) {
          return null;
      }
  };

  const activeShiftData = (nextWork && nextWork.status === 'in-progress' && nextWork.visit) ? {
    jobId: nextWork.job._id,
    visitId: nextWork.visit._id,
    correspondentName: `${nextWork.job.customer.firstName} ${nextWork.job.customer.lastName}`,
    correspondentPhoto: nextWork.job.customer.profilePhoto,
    userType: 'caregiver' as const,
    hasActiveSafetyCheck: false,
    onPinClick: (pin: string) => {
        setAutoFilledPin(pin);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } : undefined;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans animate-fade-in">
      {/* Header */}
      
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8 pb-24 sm:pb-8">
        
        {/* Welcome Section */}
        <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
              Hello, {user?.firstName} 👋
            </h1>
            <p className="text-gray-500">Here's what's happening today.</p>
        </div>

        {/* Active Shift Banner - High Priority */}
        {nextWork?.status === 'in-progress' && nextWork.visit && (
          <div className="mb-8 rounded-2xl overflow-hidden shadow-xl border border-green-100 bg-white relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-emerald-500"></div>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <span className="relative flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
                  </span>
                  <h2 className="text-xl font-bold text-gray-800">Visit in Progress</h2>
                </div>
                <div className="px-4 py-1.5 bg-green-50 text-green-700 text-sm font-bold rounded-full border border-green-100 uppercase tracking-wide">
                  Live Status
                </div>
              </div>
              <ActiveShiftView 
                job={nextWork.job} 
                visit={nextWork.visit}
                userType="caregiver"
                onShiftEnd={fetchMyJobs}
                sharedPin={autoFilledPin}
                compact={true}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content Column */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Next Scheduled Job Card */}
            {nextWork && nextWork.status !== 'in-progress' && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary-100 relative overflow-hidden group hover:shadow-md transition-all">
                {/* Decorative Background */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50 rounded-full -mr-32 -mt-32 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                  <div>
                    <span className="text-primary-600 font-bold tracking-wide uppercase text-xs mb-1 block">Up Next</span>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      {nextWork.date.toDateString() === new Date().toDateString() ? 'Today\'s Visit' : 'Upcoming Visit'}
                    </h2>
                    <p className="text-gray-500 flex items-center gap-2">
                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                       {nextWork.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  
                  {nextWork.time && (
                    <div className="mt-4 md:mt-0 flex flex-col items-center md:items-end gap-2">
                        {/* Always show the time info in a clean badge */}
                        <div className="flex items-center bg-primary-50 px-3 py-1.5 rounded-lg text-primary-800 font-bold border border-primary-100 text-sm">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            {nextWork.time}
                        </div>

                        {/* Show Countdown if relevant */}
                        {nextWork.date.toDateString() === new Date().toDateString() && getStartTimeDate(nextWork.date, nextWork.time) && new Date() < getStartTimeDate(nextWork.date, nextWork.time)! && (
                            <div className="flex flex-col items-center md:items-end animate-in fade-in slide-in-from-top-1 duration-500">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Starts In</span>
                                <CountdownTimer targetDate={getStartTimeDate(nextWork.date, nextWork.time)!} />
                            </div>
                        )}
                    </div>
                  )}
                </div>

                {/* Patient/Job Info */}
                <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200/60">
                    <img 
                        src={nextWork.job.customer.profilePhoto ? getFileUrl(nextWork.job.customer.profilePhoto) : `https://ui-avatars.com/api/?name=${nextWork.job.customer.firstName}+${nextWork.job.customer.lastName}`}
                        alt="Patient"
                        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                    />
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{nextWork.job.customer.firstName} {nextWork.job.customer.lastName}</h3>
                        <p className="text-sm text-gray-500">{nextWork.job.title} • {nextWork.job.location.city}</p>
                    </div>
                    {nextWork.job.serviceType && (
                        <div className={`ml-auto hidden sm:block px-3 py-1 rounded-full text-xs font-bold uppercase ${getServiceBadgeColor(nextWork.job.serviceType)}`}>
                            {nextWork.job.serviceType.replace('-', ' ')}
                        </div>
                    )}
                </div>

                {/* Location with Map Link */}
                <a 
                  href={
                    (nextWork.job.location.coordinates && nextWork.job.location.coordinates.length === 2)
                     ? `https://www.google.com/maps/search/?api=1&query=${nextWork.job.location.coordinates[1]},${nextWork.job.location.coordinates[0]}`
                     : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(nextWork.job.location.address + ', ' + nextWork.job.location.city)}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 mb-6 p-4 bg-primary-50/50 rounded-xl border border-primary-100 hover:bg-primary-50 transition-colors group cursor-pointer"
                >
                   <div className="bg-primary-100 p-2 rounded-lg text-primary-600 group-hover:bg-primary-200 transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                   </div>
                   <div>
                      <p className="text-xs text-primary-600 font-bold uppercase tracking-wide mb-1">Service Location</p>
                      <p className="text-gray-900 font-medium leading-snug">{nextWork.job.location.address}</p>
                      <p className="text-gray-500 text-sm">{nextWork.job.location.city}</p>
                   </div>
                   <svg className="w-5 h-5 text-gray-400 ml-auto self-center group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </a>

                {/* Notes if any */}
                {nextWork.notes && (
                  <div className="mb-6 bg-yellow-50 p-4 rounded-xl border border-yellow-100">
                    <p className="text-yellow-800 text-sm font-medium flex items-start gap-2">
                       <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                       <span className="italic">" {nextWork.notes} "</span>
                    </p>
                  </div>
                )}

                {/* Action Button */}
                {nextWork.canStart ? (
                  <button
                    onClick={() => handleStartShift(nextWork.job._id, nextWork.visitId!)}
                    className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl shadow-lg shadow-primary-200 transition-all transform active:scale-95 flex items-center justify-center gap-2 group"
                  >
                    <span>Start Shift Now</span>
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(`/jobs/${nextWork.job._id}`)}
                    className="w-full py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    View Job Details
                  </button>
                )}
              </div>
            )}

            {!nextWork && (
               <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
                      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Upcoming Visits</h3>
                  <p className="text-gray-500 max-w-sm mx-auto">You don't have any scheduled visits for today or upcoming days. Navigate to 'Jobs' to find new work.</p>
                  <button onClick={() => navigate('/caregiver/jobs')} className="mt-6 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium">Browse Jobs</button>
               </div>
            )}
            
            <PendingReviews />

          </div>

          {/* Right Sidebar - Calendar */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <h3 className="font-bold text-gray-900">Your Schedule</h3>
                </div>
                <div className="p-4">
                    <WorkScheduleCalendar jobs={myJobs} />
                </div>
            </div>

            {/* Quick Stats or Tips */}
            <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
                <div className="relative z-10">
                    <h3 className="font-bold text-lg mb-2">Pro Tip</h3>
                    <p className="text-primary-100 text-sm">Always arrive 5 minutes early to review the patient's updated care plan.</p>
                </div>
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white opacity-10 rounded-full"></div>
            </div>
          </div>
        </div>

      </div>

      <ShohAI activeShift={activeShiftData} />
    </div>
  );
};

export default CaregiverDashboard;
